import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * BackupCodeVerifyStep — the screen the user sees when the pipeline runtime
 * pauses at `mfa.verify` with `factor: "backup_code"`. The user types one
 * of the single-use backup codes they saved when they enrolled two-factor
 * authentication.
 *
 * Composition: heading + helper copy + single text input (with optional
 * remaining-codes hint + format-mismatch live-validation) + reserved
 * inline-error row + Use button. No network calls — `onSubmit` comes
 * from the parent.
 *
 * Client-side normalization: backup codes are case-insensitive and
 * humans tend to copy them with the hyphens shown at enrollment time.
 * The submitter strips spaces + hyphens, uppercases, and trims before
 * calling `onSubmit`. Verification on the server side should match
 * against the normalized form.
 *
 * Client-side format validation:
 *   - Empty input → submit disabled (no inline error noise).
 *   - Non-empty but too short / non-alphanumeric → inline "That doesn't
 *     look like a backup code" once the user blurs or presses Enter.
 *   - Threshold defaults: 8+ characters, [A-Z0-9] after normalization.
 *
 * A11y:
 *   - Input has a real `<label>` ("Backup code") + `aria-describedby`
 *     pointing at the helper text + remaining hint.
 *   - Inline error is `role="alert"` + `aria-live="polite"`; sets
 *     `aria-invalid="true"` on the input.
 *   - Enter from the input submits.
 */

export interface BackupCodeVerifyStepProps {
  /** Called when the user submits the code (Use button or Enter). */
  onSubmit: (code: string) => void | Promise<void>
  /** Optional "(N codes remaining)" hint surfaced under the input. */
  remaining?: number
  /** Inline error surfaced by the parent (e.g. "That code was already used"). */
  error?: string
  /** Set true while submit is in flight. Disables input + shows "Verifying…". */
  busy?: boolean
  /** Override the visible heading. Default: "Enter a backup code". */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /** Extra classes for the wrapper. */
  className?: string
}

/**
 * Normalize a raw user input into the canonical comparison form:
 * lowercase, alphanumeric-only. Presentation hyphens / spaces / extra
 * whitespace get stripped before submission.
 *
 * Lowercase (not uppercase) because Aegis's backup-code charset is
 * lowercase a-z + digits 2-9 (see aegis/internal/mfa/backup_codes.go
 * backupCodeCharset). The codes are hashed with bcrypt at generate-time
 * and bcrypt is case-sensitive — sending uppercase would never match
 * the stored hash. This bit a Playwright walk on 2026-05-25 (every
 * code rejected with "credential: verification failed" until we
 * dropped the .toUpperCase() call).
 *
 * If a future Aegis revision switches to a mixed-case alphabet, this
 * helper is the single place to revisit (and the placeholder text
 * "XXXX-XXXX-XXXX" should change to reflect the new alphabet).
 */
function normalize(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]/g, '')
}

const MIN_LEN = 8

export const BackupCodeVerifyStep = React.forwardRef<HTMLDivElement, BackupCodeVerifyStepProps>(
  (
    {
      onSubmit,
      remaining,
      error,
      busy = false,
      heading = 'Enter a backup code',
      helperText = 'Use one of the backup codes you saved when you set up two-factor authentication.',
      className,
    },
    ref,
  ) => {
    const [code, setCode] = React.useState('')
    // Touched = has the user committed input yet (blur / Enter)? Inline
    // format error only renders after touched to avoid scolding the user
    // mid-typing.
    const [touched, setTouched] = React.useState(false)

    const normalized = React.useMemo(() => normalize(code), [code])
    // Local format error — only shown after touched + something typed.
    const localFormatError = React.useMemo(() => {
      if (!touched) return ''
      if (normalized.length === 0) return ''
      if (normalized.length < MIN_LEN) return "That doesn't look like a backup code."
      return ''
    }, [normalized.length, touched])

    // Parent error wins over local format error — if the server rejected
    // the code, that's what the user needs to see.
    const visibleError = error || localFormatError
    // `aria-invalid` mirrors visibleError so AT users get the same signal.
    const ariaInvalid = !!visibleError

    const submitDisabled = busy || normalized.length < MIN_LEN

    const handleSubmit = React.useCallback(async () => {
      setTouched(true)
      if (busy) return
      if (normalized.length < MIN_LEN) return
      await onSubmit(normalized)
    }, [busy, normalized, onSubmit])

    const inputId = React.useId()
    const helperId = `${inputId}-helper`
    const errorId = `${inputId}-error`

    // Build aria-describedby manually — cn() runs tailwind-merge and
    // would happily strip / re-order id tokens. The describedby links
    // the input to the helper copy (if rendered) and the live-error
    // region (when an error is being announced).
    const describedBy = [helperText ? helperId : null, ariaInvalid ? errorId : null]
      .filter(Boolean)
      .join(' ')

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)}>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="text-heading-sm font-semibold text-ia-text">{heading}</h2>
          {helperText ? (
            <p id={helperId} className="text-body-sm text-ia-text-secondary">
              {helperText}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={inputId} className="text-body-sm font-medium text-ia-text">
            Backup code
          </label>
          <input
            id={inputId}
            type="text"
            // Backup codes are short opaque strings — no autofill mapping
            // is appropriate. one-time-code is the closest semantic and
            // matches what password managers offer for "MFA recovery
            // code" entries.
            autoComplete="one-time-code"
            // Common backup-code format is alphanumeric blocks (no
            // mixed-case requirement); inputmode helps mobile keyboards.
            inputMode="text"
            // Spell-check disabled so iOS / Android don't try to
            // autocorrect the user's code into a real word.
            spellCheck={false}
            autoCapitalize="characters"
            // biome-ignore lint/a11y/noAutofocus: the verify step is a single-purpose pause screen whose only meaningful action is filling this one field; matches the autoFocus the OtpDigitInput / VerificationOtpStep already set for the same reason
            autoFocus
            placeholder="XXXX-XXXX-XXXX"
            value={code}
            disabled={busy}
            onChange={(e) => setCode(e.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleSubmit()
              }
            }}
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={describedBy || undefined}
            className={cn(
              'h-12 w-full rounded-md border bg-ia-input-bg px-3',
              'font-mono text-body-md text-ia-text tabular-nums',
              'transition-[border-color,background-color] duration-150',
              'focus-visible:outline-none focus-visible:border-ring',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-ia-elevated',
              ariaInvalid
                ? 'border-ia-border-error focus-visible:border-ia-border-error'
                : 'border-ia-border',
            )}
          />
          {typeof remaining === 'number' ? (
            <p className="text-body-sm text-ia-text-secondary">
              {remaining === 1
                ? '1 code remaining. Consider generating new codes after this.'
                : `${remaining} codes remaining.`}
            </p>
          ) : null}
        </div>

        {/* Reserved error row so layout doesn't jump in/out. */}
        <div
          id={errorId}
          role="alert"
          aria-live="polite"
          className={cn(
            'min-h-5 text-center text-body-sm',
            visibleError ? 'text-ia-error' : 'text-transparent',
          )}
        >
          {visibleError || 'placeholder'}
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={submitDisabled}
          loading={busy}
          className="w-full"
        >
          {busy ? 'Verifying…' : 'Use backup code'}
        </Button>
      </div>
    )
  },
)
BackupCodeVerifyStep.displayName = 'BackupCodeVerifyStep'
